import { Check, Code, ContentCopy, Download } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { useToast } from 'hooks/use-toast';

interface HtmlSourcePanelProps {
    htmlContent: string;
}

export function HtmlSourcePanel({ htmlContent }: HtmlSourcePanelProps) {
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(htmlContent);
            setCopied(true);
            toast({
                title: 'Copied to clipboard',
                description: 'HTML source code has been copied to your clipboard.',
            });
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast({
                title: 'Copy failed',
                description: 'Failed to copy HTML to clipboard.',
                variant: 'destructive',
            });
        }
    };

    const handleDownload = () => {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `template-${Date.now()}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
            title: 'Downloaded',
            description: 'HTML source code has been downloaded.',
        });
    };

    const formatHtml = (html: string): string => {
        let formatted = '';
        let indent = 0;
        const tab = '  ';

        html.split(/(<[^>]+>)/g).forEach((node) => {
            if (node.match(/^<\/\w/)) {
                indent = Math.max(0, indent - 1);
            }

            if (node.trim()) {
                formatted += tab.repeat(indent) + node.trim() + '\n';
            }

            if (node.match(/^<\w[^>]*[^/]>$/)) {
                indent += 1;
            }
        });

        return formatted.trim();
    };

    const formattedHtml = formatHtml(htmlContent);
    const lineCount = formattedHtml.split('\n').length;
    const charCount = htmlContent.length;
    const wordCount = htmlContent
        .replace(/<[^>]*>/g, ' ')
        .trim()
        .split(/\s+/).length;

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
                <Stack
                    direction="row"
                    sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Code sx={{ fontSize: 20, color: 'text.secondary' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            HTML Source
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Tooltip title="Download HTML file">
                            <IconButton
                                size="small"
                                onClick={handleDownload}
                                aria-label="Download HTML file"
                            >
                                <Download fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCopy}
                            startIcon={copied ? <Check /> : <ContentCopy />}
                            sx={{ textTransform: 'none' }}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </Stack>
                </Stack>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1.5 }}
                >
                    {lineCount} lines · {charCount} characters · {wordCount} words
                </Typography>

                <Alert severity="info" sx={{ mt: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        What is this?
                    </Typography>
                    <Typography variant="body2">
                        This is the raw HTML code that will be saved to the backend when you save
                        this template. You can copy or download it for debugging, backup, or
                        external processing.
                    </Typography>
                </Alert>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 2,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        overflowX: 'auto',
                        whiteSpace: 'pre',
                    }}
                >
                    {formattedHtml}
                </Box>
            </Box>

            <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Backend Storage Format
                </Typography>
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        Stored as:{' '}
                        <Box
                            component="code"
                            sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}
                        >
                            htmlContent
                        </Box>{' '}
                        field
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Format: Plain HTML string
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Placeholders: Preserved as{' '}
                        <Box
                            component="code"
                            sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}
                        >
                            {'{{token}}'}
                        </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Encoding: UTF-8
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
}
